import React, { useState, useEffect, useRef } from 'react';

const LazyImage = ({ src, alt = '', className = '', style = {}, placeholder = null }) => {
  const [imageSrc, setImageSrc] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [inView, setInView] = useState(false);
  const imgRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            observer.disconnect();
          }
        });
      },
      {
        rootMargin: '50px', // Start loading 50px before entering viewport
        threshold: 0.01,
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => {
      if (imgRef.current) {
        observer.unobserve(imgRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (inView && src) {
      const img = new Image();
      img.onload = () => {
        setImageSrc(src);
        setIsLoaded(true);
      };
      img.onerror = () => {
        // On error, show a placeholder or broken image indicator
        // Use a data URI for a simple placeholder
        setImageSrc('data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="100" height="100"%3E%3Crect width="100" height="100" fill="%23aeb4b8"/%3E%3Ctext x="50" y="50" font-family="Arial" font-size="12" fill="%23553918" text-anchor="middle" dominant-baseline="middle"%3EImage%3C/text%3E%3C/svg%3E');
        setIsLoaded(true);
      };
      img.src = src;
    }
  }, [inView, src]);

  // Use placeholder if provided, otherwise use a minimal transparent SVG
  const placeholderSrc = placeholder || 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 1 1"%3E%3C/svg%3E';

  return (
    <img
      ref={imgRef}
      src={imageSrc || placeholderSrc}
      alt={alt}
      className={className}
      style={{
        ...style,
        opacity: isLoaded ? 1 : 0.3,
        // Combine opacity transition with existing transform transition from CSS
        transition: 'opacity 0.3s ease-in-out, transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
        backgroundColor: isLoaded ? 'transparent' : 'rgba(49, 35, 16, 0.05)', // Subtle placeholder background
      }}
      loading="lazy" // Native lazy loading as fallback for browsers that support it
    />
  );
};

export default LazyImage;

